class NaptonPlanExplorer extends HTMLElement {
  zoom = 1;
  points = new Map();

  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const signal = this.abort.signal;
    this.dialog = this.querySelector('.nip-dialog');
    this.viewport = this.querySelector('.nip-dialog-viewport');
    this.addEventListener('click', this.onClick, { signal });
    this.addEventListener('keydown', this.onKeydown, { signal });
    this.viewport?.addEventListener('pointerdown', this.onPointerDown, { signal });
    this.viewport?.addEventListener('pointermove', this.onPointerMove, { signal });
    this.viewport?.addEventListener('pointerup', this.onPointerEnd, { signal });
    this.viewport?.addEventListener('pointercancel', this.onPointerEnd, { signal });
    this.dialog?.addEventListener('close', () => {
      this.points.clear();
      this.querySelector('[data-plan-expand]')?.focus({ preventScroll: true });
    }, { signal });
    this.selectRoom(this.dataset.activeRoom || 'bedroom', true);
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.points.clear();
    if (this.dialog?.open) this.dialog.close();
  }

  onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button');
    if (!button || !this.contains(button)) return;
    if (button.dataset.room) {
      this.selectRoom(button.dataset.room, true);
      return;
    }
    if (button.hasAttribute('data-plan-expand')) {
      this.dialog?.showModal();
      const canvas = this.viewport?.querySelector('[data-plan-canvas]');
      canvas?.style.removeProperty('width');
      this.baseWidth = canvas?.getBoundingClientRect().width || 900;
      this.setZoom(1);
      this.centerRoom(this.viewport);
    } else if (button.hasAttribute('data-plan-close')) this.dialog?.close();
    else if (button.hasAttribute('data-plan-zoom-in')) this.setZoom(this.zoom + 0.5);
    else if (button.hasAttribute('data-plan-zoom-out')) this.setZoom(this.zoom - 0.5);
    else if (button.hasAttribute('data-plan-reset')) {
      this.setZoom(1);
      this.centerRoom(this.viewport);
    }
  };

  selectRoom(room, center) {
    const panel = [...this.querySelectorAll('[data-room-panel]')].find(item => item.dataset.roomPanel === room);
    if (!panel) return;
    this.dataset.activeRoom = room;
    for (const button of this.querySelectorAll('[data-room]')) {
      const selected = button.dataset.room === room;
      if (button.getAttribute('role') === 'tab') {
        button.setAttribute('aria-selected', String(selected));
        button.tabIndex = selected ? 0 : -1;
      } else button.setAttribute('aria-pressed', String(selected));
    }
    for (const item of this.querySelectorAll('[data-room-panel]')) item.hidden = item !== panel;
    const summary = this.querySelector('[data-plan-room-summary]');
    const label = this.querySelector('[role="tab"][aria-selected="true"]')?.textContent.trim().replace(/^0\d\s*/, '');
    if (summary) summary.textContent = label + ' · ' + (panel.querySelector('strong')?.textContent || '');
    if (center) {
      this.centerRoom(this.querySelector('.nip-map-scroll'));
      if (this.dialog?.open) this.centerRoom(this.viewport);
    }
  }

  centerRoom(viewport) {
    if (!viewport) return;
    const marker = [...viewport.querySelectorAll('[data-room]')].find(item => item.dataset.room === this.dataset.activeRoom);
    if (!marker) return;
    const left = marker.getBoundingClientRect().left - viewport.getBoundingClientRect().left + viewport.scrollLeft - viewport.clientWidth / 2 + marker.offsetWidth / 2;
    viewport.scrollTo({ left, behavior: 'instant' });
  }

  onKeydown = (event) => {
    if (!(event.target instanceof Element)) return;
    const tab = event.target.closest('[role="tab"]');
    if (!tab) return;
    const tabs = [...this.querySelectorAll('[role="tab"]')];
    const index = tabs.indexOf(tab);
    let next;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;
    event.preventDefault();
    this.selectRoom(tabs[next].dataset.room, true);
    tabs[next].focus({ preventScroll: true });
  };

  setZoom(value, focal) {
    if (!this.viewport) return;
    const next = Math.max(1, Math.min(3, value));
    const prior = this.zoom;
    const x = focal?.x ?? this.viewport.clientWidth / 2;
    const y = focal?.y ?? this.viewport.clientHeight / 2;
    const oldLeft = this.viewport.scrollLeft;
    const oldTop = this.viewport.scrollTop;
    this.zoom = next;
    const canvas = this.viewport.querySelector('[data-plan-canvas]');
    if (canvas) canvas.style.width = (this.baseWidth || 900) * next + 'px';
    this.viewport.scrollLeft = (oldLeft + x) * next / prior - x;
    this.viewport.scrollTop = (oldTop + y) * next / prior - y;
    const output = this.querySelector('[data-plan-zoom]');
    if (output) output.textContent = Math.round(next * 100) + '%';
    const minus = this.querySelector('[data-plan-zoom-out]');
    const plus = this.querySelector('[data-plan-zoom-in]');
    if (minus) minus.disabled = next <= 1;
    if (plus) plus.disabled = next >= 3;
  }

  onPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest('button')) return;
    this.points.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.viewport.setPointerCapture(event.pointerId);
    this.dragStart = { x: event.clientX, y: event.clientY, left: this.viewport.scrollLeft, top: this.viewport.scrollTop };
    if (this.points.size === 2) {
      const [a, b] = [...this.points.values()];
      this.pinch = { distance: Math.hypot(a.x - b.x, a.y - b.y), zoom: this.zoom };
    }
  };

  onPointerMove = (event) => {
    if (!this.points.has(event.pointerId)) return;
    event.preventDefault();
    this.points.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.points.size === 2 && this.pinch) {
      const [a, b] = [...this.points.values()];
      const rect = this.viewport.getBoundingClientRect();
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      this.setZoom(this.pinch.zoom * distance / Math.max(1, this.pinch.distance), { x: (a.x + b.x) / 2 - rect.left, y: (a.y + b.y) / 2 - rect.top });
    } else if (this.points.size === 1 && this.dragStart) {
      this.viewport.scrollLeft = this.dragStart.left - (event.clientX - this.dragStart.x);
      this.viewport.scrollTop = this.dragStart.top - (event.clientY - this.dragStart.y);
    }
  };

  onPointerEnd = (event) => {
    this.points.delete(event.pointerId);
    this.pinch = null;
    this.dragStart = null;
    if (this.points.size === 1) {
      const [point] = [...this.points.values()];
      this.dragStart = { x: point.x, y: point.y, left: this.viewport.scrollLeft, top: this.viewport.scrollTop };
    }
  };
}
if (!customElements.get('napton-plan-explorer')) customElements.define('napton-plan-explorer', NaptonPlanExplorer);
